import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/format";
import { Monitor, Smartphone, KeyRound, ShieldX, PenLine, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import CalendarFeedsManager from "@/components/CalendarFeedsManager";
import { CategorySignaturesSection } from "@/components/CategorySignaturesSection";

function SignatureCard({ user, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [designation, setDesignation] = useState(user?.designation || "");
  const [savingDes, setSavingDes] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingPhone, setSavingPhone] = useState(false);
  const sigUrl = user?.signature_url || "";

  useEffect(() => {
    setDesignation(user?.designation || "");
  }, [user?.designation]);
  useEffect(() => {
    setPhone(user?.phone || "");
  }, [user?.phone]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so picking the same file twice still triggers
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/me/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature uploaded — it will appear on your next quotation PDF");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your signature? Quotation PDFs will revert to the company default.")) return;
    setRemoving(true);
    try {
      await api.delete("/auth/me/signature");
      toast.success("Signature removed");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setRemoving(false); }
  };

  const saveDesignation = async () => {
    setSavingDes(true);
    try {
      await api.patch("/auth/me", { designation });
      toast.success("Designation saved");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setSavingDes(false); }
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await api.patch("/auth/me", { phone });
      toast.success("Mobile number saved");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setSavingPhone(false); }
  };

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mt-5" data-testid="signature-card">
      <CardContent className="p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1 flex items-center gap-1.5">
          <PenLine className="h-3 w-3" /> Signature on Quotations
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Upload your handwritten signature once — it will be stamped at the
          <span className="font-semibold text-foreground"> Authorised Signatory </span>
          block of every quotation PDF you generate. PNG / JPG / WEBP, max 2 MB.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold">Signature image</Label>
            <div className="mt-1.5 rounded-2xl border-2 border-dashed border-border bg-muted/30 p-4 grid place-items-center min-h-[100px]">
              {sigUrl ? (
                <img
                  src={sigUrl}
                  alt="Your signature"
                  className="max-h-20 max-w-full object-contain"
                  data-testid="signature-preview"
                />
              ) : (
                <p className="text-xs text-muted-foreground italic">No signature uploaded yet</p>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={onPick}
                  data-testid="signature-file-input"
                />
                <Button asChild variant="outline" className="rounded-2xl w-full tap" disabled={uploading}>
                  <span>
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {uploading ? "Uploading…" : sigUrl ? "Replace" : "Upload"}
                  </span>
                </Button>
              </label>
              {sigUrl && (
                <Button
                  variant="outline"
                  onClick={remove}
                  disabled={removing}
                  className="rounded-2xl text-red-600 hover:bg-red-50 hover:text-red-700 tap"
                  data-testid="signature-remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Designation (printed under your name)</Label>
            <Input
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              placeholder="e.g. Sales Engineer · Director · CEO"
              className="rounded-2xl h-11 mt-1.5"
              maxLength={120}
              data-testid="signature-designation-input"
            />
            <Button
              onClick={saveDesignation}
              disabled={savingDes || (designation || "") === (user?.designation || "")}
              className="rounded-2xl mt-2 w-full sm:w-auto tap"
              data-testid="signature-designation-save"
            >
              {savingDes ? "Saving…" : "Save designation"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2">
              Appears below your name in the signatory block on every quotation PDF.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold">Mobile number (printed in the FROM box)</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +91 98765 43210"
              className="rounded-2xl h-11 mt-1.5"
              maxLength={30}
              data-testid="profile-phone-input"
            />
            <Button
              onClick={savePhone}
              disabled={savingPhone || (phone || "") === (user?.phone || "")}
              className="rounded-2xl mt-2 w-full sm:w-auto tap"
              data-testid="profile-phone-save"
            >
              {savingPhone ? "Saving…" : "Save mobile number"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2">
              Shown with your name & email in the top-right FROM section of your quotation PDFs.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function _shortUA(ua) {
  if (!ua) return "Unknown device";
  // Pick out browser + OS; full Mozilla/5.0 chains overflow on mobile and
  // don't tell the user anything useful.
  const lower = ua.toLowerCase();
  let os = "Unknown OS";
  if (/iphone|ipad|ipod/.test(lower)) os = "iOS";
  else if (/android/.test(lower)) os = "Android";
  else if (/mac os x|macintosh/.test(lower)) os = "macOS";
  else if (/windows/.test(lower)) os = "Windows";
  else if (/linux/.test(lower)) os = "Linux";
  let browser = "Browser";
  if (/edg\//.test(lower)) browser = "Edge";
  else if (/chrome\//.test(lower)) browser = "Chrome";
  else if (/firefox\//.test(lower)) browser = "Firefox";
  else if (/safari\//.test(lower)) browser = "Safari";
  return `${browser} on ${os}`;
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loadingS, setLoadingS] = useState(true);
  const [history, setHistory] = useState([]);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  // 9.docx — keep the page compact: show only the latest 3 by default.
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const loadAll = () => {
    api.get("/auth/sessions").then((r) => setSessions(r.data.sessions)).finally(() => setLoadingS(false));
    api.get("/auth/login-history", { params: { limit: 30 } }).then((r) => setHistory(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const revoke = async (sid) => {
    if (!window.confirm("Sign out from this device?")) return;
    try { await api.delete(`/auth/sessions/${sid}`); toast.success("Session revoked"); loadAll(); } catch (err) { toast.error(fmtError(err)); }
  };

  const changePwd = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords don't match"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password updated. Other devices have been signed out.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
      loadAll();
    } catch (err) { toast.error(fmtError(err)); } finally { setSavingPwd(false); }
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1000px] mx-auto pb-28 sm:pb-10">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Account</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1 break-words">{user?.name}</h1>
        <p className="text-sm text-muted-foreground mt-2 break-all">{user?.email} · <span className="capitalize">{user?.role}</span></p>
      </header>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4 flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> Change password</p>
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold">Current password</Label><Input type="password" className="rounded-2xl h-11 mt-1.5" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current-input" /></div>
              <div><Label className="text-xs font-semibold">New password</Label><Input type="password" className="rounded-2xl h-11 mt-1.5" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new-input" /></div>
              <div><Label className="text-xs font-semibold">Confirm new password</Label><Input type="password" className="rounded-2xl h-11 mt-1.5" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm-input" /></div>
              <p className="text-[11px] text-muted-foreground">Min 10 characters with upper, lower, digit, and symbol.</p>
              <Button onClick={changePwd} disabled={savingPwd} className="w-full h-11 rounded-2xl tap" data-testid="pwd-save-button">
                {savingPwd ? "Saving…" : "Update password"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4 flex items-center gap-1.5"><Monitor className="h-3 w-3" /> Active sessions</p>
            {loadingS ? <Skeleton className="h-32" /> : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No active sessions.</p>
            ) : (
              <div className="space-y-2">
                {(showAllSessions ? sessions : sessions.slice(0, 3)).map((s) => {
                  const isMobile = /mobile|iphone|android/i.test(s.user_agent || "");
                  const Icon = isMobile ? Smartphone : Monitor;
                  const label = _shortUA(s.user_agent || s.type);
                  return (
                    <div key={s.sid || s.id} className="flex items-start sm:items-center gap-3 p-3 rounded-2xl border border-border/60 flex-wrap" data-testid={`session-${s.sid || s.id}`}>
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 sm:mt-0" />
                      <div className="flex-1 min-w-0 basis-full sm:basis-0">
                        <p className="font-semibold text-sm">{label}</p>
                        <p className="text-[11px] text-muted-foreground">
                          <span className="font-mono">{s.ip || "—"}</span>
                          {" · "}last active {fmtDateTime(s.last_activity || s.created_at)}
                        </p>
                        <p className="text-[10px] text-muted-foreground/80 mt-1 break-all line-clamp-2">{s.user_agent}</p>
                      </div>
                      {s.sid && (
                        <Button variant="outline" size="sm" className="rounded-xl tap shrink-0" onClick={() => revoke(s.sid)} data-testid={`revoke-${s.sid}`}>
                          <ShieldX className="h-3 w-3 mr-1" /> Revoke
                        </Button>
                      )}
                    </div>
                  );
                })}
                {sessions.length > 3 && (
                  <Button variant="ghost" size="sm" className="w-full rounded-xl text-xs text-primary-700"
                    onClick={() => setShowAllSessions((v) => !v)} data-testid="sessions-see-more">
                    {showAllSessions ? "Show Less" : `See More (${sessions.length - 3} more)`}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-3xl border-border/60 shadow-soft mt-5">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Recent login activity</p>
          <div className="space-y-1.5">
            {history.length === 0 ? <p className="text-sm text-muted-foreground py-4">No activity.</p> : (showAllHistory ? history : history.slice(0, 3)).map((h) => (
              <div key={h.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border/60 text-xs">
                <Badge variant="outline" className="font-mono text-[10px]">{h.action}</Badge>
                <span className="text-muted-foreground flex-1">{h.meta?.ip || h.meta?.method || ""}</span>
                <span className="text-muted-foreground">{fmtDateTime(h.timestamp)}</span>
              </div>
            ))}
            {history.length > 3 && (
              <Button variant="ghost" size="sm" className="w-full rounded-xl text-xs text-primary-700"
                onClick={() => setShowAllHistory((v) => !v)} data-testid="history-see-more">
                {showAllHistory ? "Show Less" : `See More (${history.length - 3} more)`}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <SignatureCard user={user} onChanged={refresh} />

      <CalendarFeedsManager />

      {user?.role === "admin" && <CategorySignaturesSection />}
    </div>
  );
}
